import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from 'reactstrap';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import '../styles.css';

const Calendar = () => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState({});
  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [newEvent, setNewEvent] = useState({ title: '', start: '', end: '' });
  const [eventId, setEventId] = useState(null);
  const [modal, setModal] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);

  const currentUserId = localStorage.getItem('id');

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axiosInstance.get('/event/');
        const apiEvents = response.data;

        const generateEvents = () => {
          const eventsArray = [];
          for (let year = 2022; year <= 2030; year++) {
            for (let month = 1; month <= 12; month++) {
              const date = new Date(year, month - 1, 1);
              while (date.getMonth() === month - 1) {
                if (date.getDay() === 6) {
                  eventsArray.push({ title: 'WFH', start: date.toISOString(), end: date.toISOString() });
                } else if (date.getDay() === 0) {
                  eventsArray.push({ title: 'Weekend OFF', start: date.toISOString(), end: date.toISOString() });
                }
                date.setDate(date.getDate() + 1);
              }
            }
          }
          setEvents([...apiEvents, ...eventsArray]);
        };
        generateEvents();
      } catch (err) {
        console.error(err);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    axiosInstance.get('/user/')
      .then(res => {
        setUsers(res.data);
        axiosInstance.get(`/user/${currentUserId}/`)
          .then(r => setCurrentUser(r.data))
          .catch(console.error);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRoles([res.data.active_role, res.data.assigned_role]);
        setActiveRole(res.data.active_role);
      })
      .catch(console.error);
  }, []);

  const handleAddEvent = async () => {
    try {
      const res = await axiosInstance.post('/event/', {
        title: newEvent.title,
        start: moment(newEvent.start).format('YYYY-MM-DDTHH:mm:ss'),
        end: moment(newEvent.end).format('YYYY-MM-DDTHH:mm:ss'),
        user: currentUserId,
      });
      setEvents([...events, res.data]);
      setNewEvent({ title: '', start: '', end: '' });
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateEvent = async () => {
    try {
      const res = await axiosInstance.patch(`/event/${eventId}/`, {
        title: newEvent.title,
        start: moment(newEvent.start).format('YYYY-MM-DDTHH:mm:ss'),
        end: moment(newEvent.end).format('YYYY-MM-DDTHH:mm:ss'),
      });
      setEvents(events.map(e => e.id === eventId ? res.data : e));
      setNewEvent({ title: '', start: '', end: '' });
      setModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteEvent = async () => {
    try {
      await axiosInstance.delete(`/event/${eventId}/`);
      setEvents(events.filter(e => e.id !== eventId));
      setModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEventClick = (info) => {
    setEventId(info.event.id);
    setNewEvent({ title: info.event.title, start: info.event.start, end: info.event.end });
    setModal(true);
  };

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: roles[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const isAdmin = currentUser?.role_name === 'admin' || currentUser?.role_name === 'manager' || currentUser?.role_name === 'team_lead';

  const profilePhotoUrl = currentUser?.profile_photo?.startsWith('http://') 
    ? currentUser.profile_photo.replace('http://', 'https://') 
    : currentUser?.profile_photo;

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role?.name}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>📅 Calendar</h1>
          <p>View and manage events</p>
        </div>

        <div className="dashboard-card full-width">
          <div className="calendar-wrapper">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={events}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              eventClick={handleEventClick}
              displayEventTime={false}
              height={450}

            />
          </div>
        </div>

        {isAdmin && (
          <div className="dashboard-card full-width">
            <div className="card-header">
              <span className="card-icon">➕</span>
              <h3>Add New Event</h3>
            </div>
            <div className="card-body">
              <div className="event-form">
                <div className="form-group">
                  <label>Event Title</label>
                  <input
                    type="text"
                    className="dash-input"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="Enter event title"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="datetime-local"
                      className="dash-input"
                      value={newEvent.start}
                      onChange={(e) => setNewEvent({ ...newEvent, start: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="datetime-local"
                      className="dash-input"
                      value={newEvent.end}
                      onChange={(e) => setNewEvent({ ...newEvent, end: e.target.value })}
                    />
                  </div>
                </div>
                <button className="action-btn" onClick={handleAddEvent}>
                  Add Event
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <Modal isOpen={modal} toggle={() => setModal(false)} className="event-modal">
        <ModalHeader toggle={() => setModal(false)}>Update Event</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Title</Label>
              <Input
                type="text"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Start</Label>
              <Input
                type="datetime-local"
                value={newEvent.start}
                onChange={(e) => setNewEvent({ ...newEvent, start: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>End</Label>
              <Input
                type="datetime-local"
                value={newEvent.end}
                onChange={(e) => setNewEvent({ ...newEvent, end: e.target.value })}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="warning" onClick={handleUpdateEvent}>Update</Button>
          <Button color="danger" onClick={handleDeleteEvent}>Delete</Button>
          <Button color="secondary" onClick={() => setModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      {isIdCardPopupVisible && (
        <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
          <div className="id-card-popup" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setIsIdCardPopupVisible(false)}>×</button>
            <div className="id-card-header">
              <img src="./Teamhalt.png" alt="Logo" className="id-card-logo" />
              <span>TEAMHALT PRIVATE LIMITED</span>
            </div>
            <div className="id-card-body">
              <div className="id-card-profile">
                <img src={profilePhotoUrl || '/profile.jpg'} alt="Profile" className="id-card-photo" />
                <div className="id-card-info">
                  <h4>{currentUser?.first_name} {currentUser?.last_name}</h4>
                  <p className="designation">{currentUser?.designation}</p>
                  <p><strong>DOB:</strong> {currentUser?.date_of_birth ? new Date(currentUser.date_of_birth).toLocaleDateString('en-GB').replace(/\//g, '-') : 'N/A'}</p>
                </div>
              </div>
              <hr />
              <div className="id-card-details">
                <p><strong>Emp ID:</strong> {currentUser?.employee_code || 'N/A'}</p>
                <p><strong>Department:</strong> {currentUser?.department || 'N/A'}</p>
                <p><strong>ESIC No.:</strong> {currentUser?.esic_no || 'N/A'}</p>
                <p><strong>UAN No.:</strong> {currentUser?.uan_no || 'N/A'}</p>
                <p><strong>Gender:</strong> {currentUser?.gender || 'N/A'}</p>
                <p><strong>Contact:</strong> {currentUser?.contact_number || 'N/A'}</p>
              </div>
              <div className="id-card-footer">
                https://teamhalt.com/
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
